import * as d3 from 'd3'
import Hammer from 'hammerjs'
import * as React from 'react'

import { isFirefoxDesktop } from '../shared/isMobile'
import { retinafy } from '../shared/retinafy'
import BubbleActionCreators from './actions/bubbleActionCreators'
import { BubbleSettings } from './constants/settings'
import HoverStore from './stores/hoverStore'
import { BubbleNode } from './types'

interface BubblesHoverCanvasProps {
  width: number
  height: number
  margin: { top: number; right: number; bottom: number; left: number }
  bubbles: BubbleNode[]
  onDoubleTap: HammerListener
  settings: BubbleSettings
}

class BubblesHoverCanvas extends React.Component<BubblesHoverCanvasProps> {
  canvasRef = React.createRef<HTMLCanvasElement>()
  declare canvasContext: CanvasRenderingContext2D

  componentDidMount() {
    HoverStore.addChangeListener(this._onChange)

    const canvas = this.canvasRef.current
    const context = canvas?.getContext('2d', isFirefoxDesktop() ? { willReadFrequently: true } : undefined)

    if (!canvas || !context) return

    this.canvasContext = context

    retinafy(
      canvas,
      this.canvasContext,
      this.props.width + this.props.margin.left + this.props.margin.right,
      this.props.height + this.props.margin.top + this.props.margin.bottom
    )

    const hammertime = new Hammer(canvas)
    hammertime.on('doubletap', this.props.onDoubleTap)
  }

  componentWillUnmount() {
    HoverStore.removeChangeListener(this._onChange)
  }

  _onChange = () => {
    this.setState({ hoveredNode: HoverStore.getHoveredNode() })
  }

  render() {
    return (
      <canvas
        ref={this.canvasRef}
        width={this.props.width + this.props.margin.left + this.props.margin.right}
        height={this.props.height + this.props.margin.top + this.props.margin.bottom}
        style={{ position: 'absolute', top: 0, left: 0 }}
        onMouseMove={this._onMouseMove}
      />
    )
  }

  _onMouseMove = (e: React.MouseEvent) => {
    const { settings } = this.props
    var hoverContext = this.canvasContext,
      data = this.props.bubbles,
      width = this.props.width,
      height = this.props.height,
      margin = this.props.margin

    var hovered = this._getHoveredNode(e)
    const canvas = this.canvasRef.current

    if (canvas) {
      canvas.style.cursor = hovered ? 'pointer' : 'default'
    }

    if (HoverStore.getHoveredNode() !== hovered) {
      hoverContext.clearRect(0, 0, width + margin.left + margin.right, height + margin.top + margin.bottom)
      if (hovered) {
        hoverContext.save()
        hoverContext.lineWidth = 1.1
        hoverContext.textAlign = 'center'
        hoverContext.textBaseline = 'middle'
        hoverContext.translate(margin.left, margin.top)
        var d = hovered

        var clr = d.sc
        var rgb = d3.rgb(clr)
        rgb.r = Math.round(rgb.r + 255 * 0.15)
        rgb.g = Math.round(rgb.g + 255 * 0.15)
        rgb.b = Math.round(rgb.b + 255 * 0.15)
        var fillStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 0.8 + ')'

        rgb.r = Math.round(rgb.r * 0.9 + 255 * 0.3)
        rgb.g = Math.round(rgb.g * 0.9 + 255 * 0.3)
        rgb.b = Math.round(rgb.b * 0.9 + 255 * 0.3)
        var strokeStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 1 + ')'

        hoverContext.beginPath()
        hoverContext.arc(d.sx, d.sy, d.sr, 0, 2 * Math.PI, true)
        hoverContext.fillStyle = fillStyle
        hoverContext.strokeStyle = strokeStyle
        hoverContext.stroke()

        this._renderText(hoverContext, data, hovered, clr, strokeStyle, settings)
        hoverContext.restore()
      }
    }

    BubbleActionCreators.setHoveredNode(hovered)
  }

  _renderText = (
    context: CanvasRenderingContext2D,
    data: BubbleNode[],
    d: BubbleNode,
    clr: string,
    strokeStyle: string,
    settings: BubbleSettings
  ) => {
    var showText = true
    var px = d.sx,
      py = d.sy,
      pr = d.sr + (d.sr > 10 ? 0 : 15),
      ax1 = px - pr,
      ax2 = px + pr,
      ay1 = py - pr,
      ay2 = py + pr
    for (var j = 0; j < data.length; j++) {
      var d2 = data[j]
      // eslint-disable-next-line eqeqeq
      if (d == d2) {
        continue
      }

      var bpx = d2.sx,
        bpy = d2.sy,
        bpr = d2.sr,
        bx1 = bpx - bpr,
        bx2 = bpx + bpr,
        by1 = bpy - bpr,
        by2 = bpy + bpr

      if (!(ax1 > bx2 || bx1 > ax2 || ay1 > by2 || by1 > ay2)) {
        showText = false
        break
      }
    }

    if (showText) {
      if (d.sr > 10) {
        var rgb = d3.rgb(clr)
        rgb.r = Math.round(rgb.r * 0.9 + 255 * 0.55)
        rgb.g = Math.round(rgb.g * 0.9 + 255 * 0.55)
        rgb.b = Math.round(rgb.b * 0.9 + 255 * 0.55)
        // eslint-disable-next-line @typescript-eslint/no-redeclare
        var strokeStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 1 + ')'

        context.font = '12px Lato'
        if (settings.bubble.textShadow) {
          context.fillStyle = settings.bubble.textShadow
          context.fillText(d.ticker, px + 1, py + 1)
        }
        context.fillStyle = settings.bubble.color ?? strokeStyle
        context.fillText(d.ticker, px, py)
      } else {
        context.font = '11px Lato'
        if (settings.textShadow) {
          context.fillStyle = settings.textShadow
          context.fillText(d.ticker, px + 1, py + d.sr + 11)
        }
        context.fillStyle = settings.color ?? strokeStyle
        context.fillText(d.ticker, px, py + d.sr + 10)
      }
    }
  }

  _getHoveredNode = (e: React.MouseEvent) => {
    var data = this.props.bubbles
    var p = this._getArea(e)
    p.x -= this.props.margin.left
    p.y -= this.props.margin.top
    var hovered
    for (var i = data.length - 1; i >= 0; i--) {
      var d = data[i]
      if (Math.pow(p.x - d.sx, 2) + Math.pow(p.y - d.sy, 2) < Math.pow(d.sr, 2)) {
        hovered = d
        break
      }
    }
    return hovered
  }

  _getArea = (e: React.MouseEvent) => {
    // @ts-ignore
    var rect = e.target.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }
}

export default BubblesHoverCanvas
